import type { SearchLocation } from '~/modules/search/type/api';
import type { MetaBreadcrumb } from '~/modules/category/type';

/** Root path for the professionals directory; location hubs hang directly off it. */
const PROFESSIONALS_ROOT = '/professionals';

/**
 * Most specific human-readable place name for a resolved location, used for
 * breadcrumb labels and location-aware titles. Falls back from the finest
 * granularity (suburb) up to country so sparse location data still yields a label.
 */
export const getLocationDisplayName = (
    location: SearchLocation | undefined,
): string => {
    if (!location) return '';
    return (
        location.suburb ||
        location.city ||
        location.district ||
        location.region ||
        location.state ||
        location.country ||
        ''
    );
};

/**
 * "{City}, {State}" style label when a broader administrative area is available,
 * otherwise just the place name. Used for richer location-aware page titles.
 */
export const getLocationRegionName = (
    location: SearchLocation | undefined,
): string => {
    if (!location) return '';
    const place = getLocationDisplayName(location);
    const broader = location.state || location.region;
    if (broader && broader !== place) {
        return `${place}, ${broader}`;
    }
    return place;
};

/**
 * Geographic leaf crumb for a category x location or location hub page. The href
 * points at the current location page (`{basePath}/location-{slug}`) so the
 * BreadcrumbList and visible trail both carry the place entity. Returns null when
 * there is no resolved location or slug (graceful no-op on non-location pages).
 */
export const buildLocationBreadcrumb = (
    location: SearchLocation | undefined,
    basePath: string,
    locationSlug: string | undefined,
): MetaBreadcrumb | null => {
    const label = getLocationDisplayName(location);
    if (!label || !locationSlug) return null;

    const base = basePath || PROFESSIONALS_ROOT;
    return {
        label,
        href: `${base}/location-${locationSlug}`,
    };
};

/**
 * Returns the directory page meta with the location crumb appended as the leaf,
 * or unchanged when there is no resolved location/slug. Owns the immutable append
 * so callers don't reimplement the spread (and so non-location pages are a no-op).
 */
export const withLocationBreadcrumb = <
    T extends { breadcrumbs: MetaBreadcrumb[] },
>(
    directoryPageMeta: T,
    location: SearchLocation | undefined,
    basePath: string,
    locationSlug: string | undefined,
): T => {
    const crumb = buildLocationBreadcrumb(location, basePath, locationSlug);
    if (!crumb) return directoryPageMeta;

    return {
        ...directoryPageMeta,
        breadcrumbs: [...directoryPageMeta.breadcrumbs, crumb],
    };
};

/** True when the URL is a category-less location hub (e.g. /professionals/location-austin). */
export const isLocationHub = (
    basePath: string,
    isLocationPage: boolean,
): boolean => basePath === PROFESSIONALS_ROOT && isLocationPage;

/** Drops the current page from a mesh link list so a page never links to itself. */
const dropSelfLink = <TLink extends { Link: string }>(
    items: TLink[],
    currentPath: string,
): TLink[] => items.filter((item) => item.Link !== currentPath);

interface LocationMeshDataInput<
    TMeta extends { breadcrumbs: MetaBreadcrumb[] },
    TCategory extends { Link: string },
    TLocation extends { Link: string },
> {
    directoryPageMeta: TMeta;
    appliedLocation: SearchLocation | undefined;
    basePath: string;
    /**
     * Slug from a path `location-{slug}` segment, or undefined. Only set for true
     * location pages (never for `?location=` query filters), so it gates the
     * breadcrumb, hub flag and mesh blocks to path-based location pages only.
     */
    locationSlug: string | undefined;
    /** Current request pathname, used to drop the self-link from mesh lists. */
    currentPath: string;
    categoriesInLocation: TCategory[];
    /** Curated "browse by location" links for the current category. */
    categoryLocations: TLocation[];
}

/**
 * Assembles the location-mesh slice of the directory loader's data: the
 * breadcrumb-augmented meta, hub flag, place display name, and the sibling
 * categories and browse-by-location links with the current page removed (so a
 * location page never links to itself). Returns inert defaults on non-location
 * pages. All location treatment is gated on `locationSlug` (path-based only).
 */
export const buildLocationMeshData = <
    TMeta extends { breadcrumbs: MetaBreadcrumb[] },
    TCategory extends { Link: string },
    TLocation extends { Link: string },
>({
    directoryPageMeta,
    appliedLocation,
    basePath,
    locationSlug,
    currentPath,
    categoriesInLocation,
    categoryLocations,
}: LocationMeshDataInput<TMeta, TCategory, TLocation>): {
    directoryPageMeta: TMeta;
    locationHub: boolean;
    appliedLocationName: string;
    categoriesInLocation: TCategory[];
    categoryLocations: TLocation[];
} => ({
    directoryPageMeta: withLocationBreadcrumb(
        directoryPageMeta,
        appliedLocation,
        basePath,
        locationSlug,
    ),
    locationHub: isLocationHub(basePath, !!locationSlug),
    appliedLocationName: getLocationDisplayName(appliedLocation),
    categoriesInLocation: dropSelfLink(categoriesInLocation, currentPath),
    categoryLocations: dropSelfLink(categoryLocations, currentPath),
});
